import { MarketplaceTag, SearchIndexCourseFilters, SystemTag } from '@shared/types';
import { FunctionComponent, useCallback, useMemo, MouseEvent } from 'react';
import HouseLaptopSolid from '../../../../components/Icons/HouseLaptopSolidIcon';
import LocationDotSolid from '../../../../components/Icons/LocationDotSolidIcon';
import Badge from '../Badge';
import styles from './styles.module.scss';
import BlueprintIcon from '../../../../components/BlueprintIcon';
import TagSolidIcon from '../../../../components/Icons/TagSolidIcon';
import toTitleCase from '../../../../services/strings/toTitleCase';
import { UrlObject, useRouterStatePart } from '../../../../hooks/useRouterState';
import { useMarketplaceTags } from '../../../../hooks/useMarketplaceTags';
import { useCategories } from '../../../../hooks/useCategories';
import { FolderSolidIcon, StarSolidIcon } from '../../../../components/Icons';
import { WebCourseSearchFilters } from '../../../../services/search/course/useCourseSearch';
import classNames from 'classnames';

export type BadgesProps = {
    filters: WebCourseSearchFilters;
    setFilters: (newFilters: WebCourseSearchFilters) => void;
    getFilterLink: (newFilters: WebCourseSearchFilters) => UrlObject;
    search?: string;
    resetSearch: () => void;
    className?: string;
};

const Badges: FunctionComponent<BadgesProps> = ({
    filters,
    setFilters,
    getFilterLink,
    search,
    resetSearch,
    className,
}) => {
    return (
        <div className={classNames(styles.badges, className)}>
            <Search search={search} resetSearch={resetSearch} />
            <Categories filters={filters} setFilters={setFilters} getFilterLink={getFilterLink} />
            <Tags filters={filters} setFilters={setFilters} getFilterLink={getFilterLink} />
            <Deliveries filters={filters} setFilters={setFilters} getFilterLink={getFilterLink} />
            <Location filters={filters} setFilters={setFilters} getFilterLink={getFilterLink} />
            <MinimumRating
                filters={filters}
                setFilters={setFilters}
                getFilterLink={getFilterLink}
            />
        </div>
    );
};

type FacetBadgesProps = {
    filters: WebCourseSearchFilters;
    setFilters: (newFilters: WebCourseSearchFilters) => void;
    getFilterLink: (newFilters: WebCourseSearchFilters) => UrlObject;
};

const Categories = ({ filters, setFilters, getFilterLink }: FacetBadgesProps) => {
    const [categories, setCategories, getCategoryChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'category_slug'
    );
    const { data } = useCategories(
        {
            category_refs: [],
            category_slugs: categories || [],
        },
        {
            keepPreviousData: true,
        }
    );

    return (
        <>
            {categories?.map((categorySlug) => {
                const change = categories.filter((selected) => selected !== categorySlug);
                const link = getCategoryChangeLink(change);
                const onClick = (event: MouseEvent) => {
                    event.preventDefault();
                    setCategories(change);
                };
                const name = getDisplayNameBySlug(categorySlug, data || []);
                return (
                    <Badge
                        key={categorySlug}
                        className={styles.badge}
                        title={name}
                        href={link}
                        onClick={onClick}
                        icon={<FolderSolidIcon />}
                    />
                );
            })}
        </>
    );
};

const isSystemTag = (tag: MarketplaceTag): tag is SystemTag => {
    return tag.is_system === true;
};

const Tags = ({ filters, setFilters, getFilterLink }: FacetBadgesProps) => {
    const [tags, setTags, getTagsChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'marketplace_tag_slug'
    );
    const { data: marketplaceTags } = useMarketplaceTags(
        {
            tag_refs: [],
            tag_slugs: tags || [],
        },
        {
            keepPreviousData: true,
        }
    );
    const systemTags = useMemo(() => {
        if (!marketplaceTags || !Array.isArray(marketplaceTags)) {
            return [];
        }
        return marketplaceTags.filter(isSystemTag);
    }, [marketplaceTags]);
    return (
        <>
            {tags?.map((slug) => {
                const change = tags.filter((selected) => selected !== slug);
                const link = getTagsChangeLink(change);
                const onClick = (event: MouseEvent) => {
                    event.preventDefault();
                    setTags(change);
                };
                const name = getDisplayNameBySlug(slug, systemTags);
                return (
                    <Badge
                        key={slug}
                        className={styles.badge}
                        title={name}
                        onClick={onClick}
                        href={link}
                        icon={<TagSolidIcon />}
                    />
                );
            })}
        </>
    );
};

const getDisplayNameBySlug = (
    slug: string,
    items: Array<{ slug: string; name: string }>
): string => {
    const item = items.find((item) => {
        return item.slug === slug;
    });

    if (item) {
        return item.name;
    }

    return toTitleCase(slug);
};

const Deliveries = ({ filters, setFilters, getFilterLink }: FacetBadgesProps) => {
    const [delivery, setDelivery, getDeliveryChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'delivery'
    );

    const changeLink = useMemo(() => {
        return getDeliveryChangeLink(undefined);
    }, [getDeliveryChangeLink]);

    const onClick = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            setDelivery(undefined);
        },
        [setDelivery]
    );

    if (!delivery) {
        return null;
    }

    return (
        <Badge
            className={styles.badge}
            title={toTitleCase(delivery)}
            href={changeLink}
            onClick={onClick}
            icon={<HouseLaptopSolid />}
        />
    );
};

type SearchProps = {
    search?: string;
    resetSearch: () => void;
};

const Search: FunctionComponent<SearchProps> = ({ search, resetSearch }) => {
    const onClick = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            resetSearch();
        },
        [resetSearch]
    );

    if (!search || search === '' || typeof search !== 'string') {
        return null;
    }

    return (
        <Badge
            className={styles.badge}
            title={search}
            href={''}
            onClick={onClick}
            icon={<BlueprintIcon icon="Search" />}
        />
    );
};

const Location: FunctionComponent<FacetBadgesProps> = ({ filters, setFilters, getFilterLink }) => {
    const [location, setLocation, getLocationChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'location'
    );
    const title = useMemo((): string => {
        if (!location) {
            return '';
        }
        if ('known_location' in location) {
            return location.known_location;
        } else {
            return `Near ${location.query}`;
        }
    }, [location]);
    if (!location) {
        return null;
    }
    const change = undefined;
    const link = getLocationChangeLink(change);
    const onClick = (event: MouseEvent) => {
        event.preventDefault();
        setLocation(change);
    };
    return (
        <Badge
            className={styles.badge}
            title={title}
            href={link}
            onClick={onClick}
            icon={<LocationDotSolid />}
        />
    );
};

const MinimumRating: FunctionComponent<FacetBadgesProps> = ({
    filters,
    setFilters,
    getFilterLink,
}) => {
    const [minimumRating, setMinimumRating, getMinimumRatingChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'minimum_rating'
    );
    const onClick = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            setMinimumRating(undefined);
        },
        [setMinimumRating]
    );
    if (!minimumRating) {
        return null;
    }
    return (
        <Badge
            className={styles.badge}
            title={`Rated ${minimumRating} or above`}
            href={getMinimumRatingChangeLink(undefined)}
            onClick={onClick}
            icon={<StarSolidIcon />}
        />
    );
};

export default Badges;
